import { useEffect, useState, useCallback, useRef } from "react";
import { ordersAPI, assignmentAPI } from "../services/api";
import { useAuth } from "../context/AuthContext";
import { format } from "date-fns";
import { RefreshCw, Eye, UserCheck, AlertTriangle, X, Star } from "lucide-react";
import { BranchBadge, BranchTabs, useBranchSplit } from "../components/ui/BranchTabs";
import toast from "react-hot-toast";

const STATUS_OPTIONS  = ["","payment_pending","pending","needs_assignment","accepted","rider_assigned","picked_up","on_the_way","delivered","cancelled"];
const SERVICE_OPTIONS = ["","food","parcel","pharmacy","grocery","other"];

const statusBadge = (s) => {
  const map = {
    payment_pending:"badge-payment",  needs_assignment:"badge-needs-assign",
    pending:"badge-pending",          accepted:"badge-active",
    rider_assigned:"badge-on-way",    picked_up:"badge-picked-up",
    on_the_way:"badge-on-way",        delivered:"badge-delivered",
    cancelled:"badge-cancelled",
  };
  return <span className={`badge ${map[s]||"badge-pending"}`}>{s?.replace(/_/g," ")}</span>;
};

const paymentBadge = (paymentStatus, paymentMethod) => {
  if (!paymentMethod || paymentMethod === "cash") {
    return <span className="badge badge-cash">💵 cash</span>;
  }
  const map = { paid:"badge-paid", pending:"badge-payment", failed:"badge-cancelled" };
  const icon = { paid:"✅", pending:"⏳", failed:"❌" }[paymentStatus] || "⏳";
  return (
    <span style={{display:"flex",gap:4,alignItems:"center",flexWrap:"wrap"}}>
      <span className={`badge ${map[paymentStatus]||"badge-payment"}`}>{icon} {paymentStatus||"pending"}</span>
      <span className="badge badge-method">{paymentMethod?.replace(/_/g," ")}</span>
    </span>
  );
};

// ── Assign Rider Modal ────────────────────────────────────────────────────────
function AssignModal({ order, onClose, onAssigned }) {
  const [riders, setRiders]     = useState([]);
  const [loading, setLoading]   = useState(true);
  const [selected, setSelected] = useState(null);
  const [saving, setSaving]     = useState(false);

  useEffect(() => {
    assignmentAPI.availableRiders(order.id)
      .then(r => setRiders(r.data.data || []))
      .catch(() => toast.error("Failed to load riders"))
      .finally(() => setLoading(false));
  }, [order.id]);

  const handleAssign = async () => {
    if (!selected) return;
    setSaving(true);
    try {
      await assignmentAPI.assign(order.id, selected.id);
      toast.success(`✅ ${selected.name} assigned to Order #${order.id}`);
      onAssigned();
      onClose();
    } catch (e) {
      toast.error(e.response?.data?.message || "Assignment failed");
    } finally { setSaving(false); }
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal" style={{maxWidth:520}} onClick={e=>e.stopPropagation()}>
        <div className="modal-header">
          <div>
            <div className="modal-title">Assign Rider</div>
            <div style={{fontSize:12,color:"var(--text3)",marginTop:2}}>
              Order #{order.id} · {order.pickup_address} → {order.dropoff_address}
            </div>
          </div>
          <button className="btn btn-ghost btn-icon btn-sm" onClick={onClose}><X size={16}/></button>
        </div>
        <div className="modal-body">
          {loading ? (
            <div className="loading-center"><div className="spinner"/><span>Loading available riders...</span></div>
          ) : riders.length === 0 ? (
            <div className="empty-state" style={{padding:"32px 0"}}>
              <div style={{fontSize:36,marginBottom:8}}>🏍️</div>
              <p style={{fontWeight:700}}>No riders available right now</p>
              <p style={{color:"var(--text3)",fontSize:13}}>Ask a rider to go online, then try again.</p>
            </div>
          ) : (
            <>
              <p style={{color:"var(--text2)",fontSize:13,marginBottom:14}}>
                Select an available rider to assign to this order:
              </p>
              <div style={{display:"flex",flexDirection:"column",gap:8,maxHeight:320,overflowY:"auto"}}>
                {riders.map(r => {
                  const isSelected = selected?.id === r.id;
                  return (
                    <div
                      key={r.id}
                      onClick={() => setSelected(r)}
                      style={{
                        display:"flex", alignItems:"center", gap:12,
                        padding:"12px 14px", borderRadius:10, cursor:"pointer",
                        border:`1.5px solid ${isSelected?"var(--accent)":"var(--border)"}`,
                        background: isSelected ? "rgba(245,166,35,0.08)" : "var(--bg3)",
                        transition:"all 0.15s",
                      }}
                    >
                      <div style={{
                        width:38,height:38,borderRadius:8,flexShrink:0,
                        background: isSelected ? "var(--accent)" : "var(--bg2)",
                        display:"flex",alignItems:"center",justifyContent:"center",
                        fontWeight:800,fontSize:15,
                        color: isSelected ? "#000" : "var(--text2)",
                      }}>
                        {r.name?.[0]?.toUpperCase()}
                      </div>
                      <div style={{flex:1,minWidth:0}}>
                        <div style={{fontWeight:700,fontSize:14}}>{r.name}</div>
                        <div style={{fontSize:11,color:"var(--text3)",display:"flex",gap:8,marginTop:2,flexWrap:"wrap"}}>
                          <span style={{textTransform:"capitalize"}}>🏍 {r.vehicle_type}</span>
                          <span>📦 {r.total_deliveries} deliveries</span>
                          {r.branch_name && <BranchBadge branchName={r.branch_name} isMyBranch={!!r.is_my_branch} compact/>}
                        </div>
                      </div>
                      <div style={{textAlign:"right",flexShrink:0}}>
                        <div style={{color:"var(--accent)",fontWeight:700,display:"flex",alignItems:"center",gap:3}}>
                          <Star size={12} fill="var(--accent)"/>
                          {parseFloat(r.rating||0).toFixed(1)}
                        </div>
                        <div style={{fontSize:10,color:"var(--green)",marginTop:2}}>● Online</div>
                      </div>
                      {isSelected && (
                        <div style={{color:"var(--accent)",fontWeight:800,fontSize:18,flexShrink:0}}>✓</div>
                      )}
                    </div>
                  );
                })}
              </div>
            </>
          )}
        </div>
        {riders.length > 0 && (
          <div className="modal-footer">
            <button className="btn btn-ghost" onClick={onClose}>Cancel</button>
            <button
              className="btn btn-primary"
              onClick={handleAssign}
              disabled={!selected || saving}
            >
              {saving ? "Assigning..." : selected ? `Assign ${selected.name}` : "Select a rider first"}
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

// ── Unassigned Queue Panel ────────────────────────────────────────────────────
function UnassignedQueue({ onAssigned, refreshKey }) {
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(false);
  const [assigning, setAssigning] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await assignmentAPI.unassigned();
      setOrders(r.data.data || []);
    } catch { /* silent */ }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load, refreshKey]);

  if (!loading && orders.length === 0) return null;

  return (
    <>
      <div style={{
        display:"flex",alignItems:"center",gap:12,
        background:"rgba(239,68,68,0.08)",border:"1px solid rgba(239,68,68,0.25)",
        borderRadius:10,padding:"12px 16px",marginBottom:16,
      }}>
        <AlertTriangle size={18} style={{color:"var(--red)",flexShrink:0}}/>
        <div style={{flex:1}}>
          <div style={{fontWeight:700,color:"var(--text)",fontSize:13}}>
            {orders.length} order{orders.length>1?"s":""} need{orders.length===1?"s":""} a rider
          </div>
          <div style={{color:"var(--text3)",fontSize:12,marginTop:1}}>
            Auto-dispatch found no available riders. Assign manually below.
          </div>
        </div>
        <button className="btn btn-ghost btn-sm" onClick={load}><RefreshCw size={13}/></button>
      </div>

      <div className="card" style={{marginBottom:20,border:"1px solid rgba(239,68,68,0.2)"}}>
        <div className="card-header">
          <span className="card-title" style={{color:"var(--red)"}}>🚨 Needs Assignment</span>
          <span style={{fontSize:12,color:"var(--text3)"}}>{orders.length} waiting</span>
        </div>

        {/* Mobile cards */}
        <div className="mobile-cards">
          {loading
            ? <div className="loading-center"><div className="spinner"/></div>
            : orders.map(o => (
            <div key={o.id} className="mobile-card" style={{borderLeft:"3px solid var(--red)"}}>
              <div style={{display:"flex",justifyContent:"space-between",alignItems:"flex-start",marginBottom:8}}>
                <div>
                  <span className="mono" style={{fontWeight:700,color:"var(--red)",fontSize:14}}>#{o.id}</span>
                  <span style={{marginLeft:8,fontSize:13,fontWeight:600}}>{o.customer_name||"—"}</span>
                </div>
                <span className="mono" style={{color:"var(--accent)",fontWeight:700}}>GHS {parseFloat(o.price||0).toFixed(2)}</span>
              </div>
              <div style={{fontSize:12,color:"var(--text2)",marginBottom:4}}>
                <div>📍 {o.pickup_address}</div>
                <div>🏁 {o.dropoff_address}</div>
              </div>
              <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginTop:8}}>
                <div style={{fontSize:11,color:"var(--text3)"}}>
                  <BranchBadge branchName={o.branch_name} isMyBranch={!!o.is_my_branch} compact/>
                  <span style={{marginLeft:8,textTransform:"capitalize"}}>{o.service_type}</span>
                </div>
                <button className="btn btn-primary btn-sm" style={{gap:5,display:"flex",alignItems:"center"}} onClick={() => setAssigning(o)}>
                  <UserCheck size={13}/> Assign
                </button>
              </div>
            </div>
          ))}
        </div>

        {/* Desktop table */}
        <div className="desktop-table table-wrap">
          <table>
            <thead>
              <tr>
                <th>Order</th><th>Customer</th><th>Service</th><th>Branch</th>
                <th>Route</th><th>Price</th><th>Waiting</th><th>Action</th>
              </tr>
            </thead>
            <tbody>
              {loading
                ? <tr><td colSpan={8}><div className="loading-center"><div className="spinner"/></div></td></tr>
                : orders.map(o => (
                <tr key={o.id} style={{background:"rgba(239,68,68,0.03)"}}>
                  <td className="mono" style={{fontWeight:700,color:"var(--red)"}}>#{o.id}</td>
                  <td>
                    <div className="fw-bold" style={{fontSize:13}}>{o.customer_name||"—"}</div>
                    <div style={{fontSize:11,color:"var(--text3)"}}>{o.customer_phone}</div>
                  </td>
                  <td style={{textTransform:"capitalize",color:"var(--text2)"}}>{o.service_type}</td>
                  <td><BranchBadge branchName={o.branch_name} isMyBranch={!!o.is_my_branch} compact/></td>
                  <td style={{fontSize:12}}>
                    <div style={{color:"var(--text2)",maxWidth:160,overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>📍 {o.pickup_address}</div>
                    <div style={{color:"var(--text3)",maxWidth:160,overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>🏁 {o.dropoff_address}</div>
                  </td>
                  <td className="mono" style={{color:"var(--accent)",fontWeight:700}}>GHS {parseFloat(o.price||0).toFixed(2)}</td>
                  <td style={{fontSize:11,color:"var(--text3)"}}>{o.created_at ? format(new Date(o.created_at),"HH:mm") : "—"}</td>
                  <td>
                    <button className="btn btn-primary btn-sm" style={{gap:5,display:"flex",alignItems:"center"}} onClick={() => setAssigning(o)}>
                      <UserCheck size={13}/> Assign
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {assigning && (
        <AssignModal
          order={assigning}
          onClose={() => setAssigning(null)}
          onAssigned={() => { load(); onAssigned(); }}
        />
      )}
    </>
  );
}

// ── Main Orders Page ──────────────────────────────────────────────────────────
export default function Orders() {
  const { user: me } = useAuth();
  const isBranchMgr   = me?.role === "branch_manager";

  const [orders, setOrders]       = useState([]);
  const [total, setTotal]         = useState(0);
  const [page, setPage]           = useState(1);
  const [loading, setLoading]     = useState(false);
  const [selected, setSelected]   = useState(null);
  const [filters, setFilters]     = useState({ status:"", service_type:"" });
  const [newStatus, setNewStatus] = useState("");
  const [tab, setTab]             = useState("mine");
  const [queueKey, setQueueKey]   = useState(0);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await ordersAPI.list({ page, limit: 50, ...filters });
      setOrders(r.data.data || []);
      setTotal(r.data.pagination?.total || 0);
    } catch { toast.error("Failed to load orders"); }
    finally { setLoading(false); }
  }, [page, filters]);

  useEffect(() => { load(); }, [load]);

  const { mine, others, visible } = useBranchSplit(orders, isBranchMgr ? tab : "all");

  const handleStatusUpdate = async () => {
    if (!newStatus || !selected) return;
    try {
      await ordersAPI.updateStatus(selected.id, newStatus);
      toast.success(`Order #${selected.id} → ${newStatus}`);
      setSelected(null); load();
    } catch (e) { toast.error(e.response?.data?.message || "Failed"); }
  };

  const pages = Math.ceil(total / 50);

  return (
    <div>
      <style>{`
        .mobile-cards   { display: none; }
        .desktop-table  { display: block; }
        .mobile-card {
          padding: 14px 16px;
          border-bottom: 1px solid var(--border);
        }
        .mobile-card:last-child { border-bottom: none; }
        @media (max-width: 768px) {
          .mobile-cards  { display: block; }
          .desktop-table { display: none; }
        }
      `}</style>

      <div className="page-header">
        <div>
          <div className="page-title">Orders</div>
          <div className="page-subtitle">{total.toLocaleString()} total orders</div>
        </div>
        <button className="btn btn-ghost btn-sm" onClick={() => { load(); setQueueKey(k=>k+1); }}>
          <RefreshCw size={14}/> Refresh
        </button>
      </div>

      <UnassignedQueue onAssigned={() => { load(); setQueueKey(k=>k+1); }} refreshKey={queueKey}/>

      <BranchTabs activeTab={tab} onChange={setTab} myCount={mine.length} otherCount={others.length}/>

      {isBranchMgr && tab === "others" && (
        <div style={{display:"flex",alignItems:"flex-start",gap:12,background:"rgba(90,90,114,0.1)",border:"1px solid rgba(90,90,114,0.2)",borderRadius:10,padding:"12px 16px",marginBottom:16,fontSize:13}}>
          <span style={{fontSize:20,flexShrink:0}}>💡</span>
          <div>
            <div style={{fontWeight:700,color:"var(--text)",marginBottom:3}}>Helping a customer from another branch?</div>
            <div style={{color:"var(--text2)"}}>You can view any order's full details here. Status updates on foreign orders are restricted to the owning branch or an admin.</div>
          </div>
        </div>
      )}

      <div className="filters-bar">
        <select className="select" value={filters.status} onChange={e=>{setFilters(f=>({...f,status:e.target.value}));setPage(1);}}>
          {STATUS_OPTIONS.map(s=><option key={s} value={s}>{s?s.replace(/_/g," "):"All statuses"}</option>)}
        </select>
        <select className="select" value={filters.service_type} onChange={e=>{setFilters(f=>({...f,service_type:e.target.value}));setPage(1);}}>
          {SERVICE_OPTIONS.map(s=><option key={s} value={s}>{s||"All services"}</option>)}
        </select>
      </div>

      <div className="card">
        {loading
          ? <div className="loading-center"><div className="spinner"/><span>Loading...</span></div>
          : (
          <>
            {/* Mobile order cards */}
            <div className="mobile-cards">
              {visible.length === 0
                ? <div className="empty-state"><p>No orders found</p></div>
                : visible.map(o => {
                  const isForeign = isBranchMgr && !o.is_my_branch;
                  return (
                    <div key={o.id} className="mobile-card" style={{background:isForeign?"rgba(90,90,114,0.04)":o.status==="needs_assignment"?"rgba(239,68,68,0.04)":undefined}}>
                      <div style={{display:"flex",justifyContent:"space-between",alignItems:"flex-start",marginBottom:6}}>
                        <div style={{display:"flex",alignItems:"center",gap:8}}>
                          <span className="mono" style={{fontWeight:700,fontSize:13,color:o.status==="needs_assignment"?"var(--red)":undefined}}>#{o.id}</span>
                          {statusBadge(o.status)}
                        </div>
                        <span className="mono" style={{color:"var(--accent)",fontWeight:700}}>GHS {parseFloat(o.price||0).toFixed(2)}</span>
                      </div>
                      <div style={{fontWeight:600,fontSize:13,marginBottom:2}}>{o.customer_name||"—"} <span style={{color:"var(--text3)",fontWeight:400,fontSize:11}}>{o.customer_phone}</span></div>
                      <div style={{fontSize:12,color:"var(--text2)",marginBottom:6}}>
                        <div style={{overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>📍 {o.pickup_address}</div>
                        <div style={{overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>🏁 {o.dropoff_address}</div>
                      </div>
                      <div style={{display:"flex",justifyContent:"space-between",alignItems:"center"}}>
                        <div style={{display:"flex",gap:6,alignItems:"center",flexWrap:"wrap"}}>
                          {paymentBadge(o.payment_status, o.payment_method)}
                          <span style={{fontSize:11,color:"var(--text3)",textTransform:"capitalize"}}>{o.service_type}</span>
                        </div>
                        <button className="btn btn-ghost btn-icon btn-sm" onClick={()=>{setSelected({...o,isForeign});setNewStatus(o.status);}}>
                          <Eye size={14}/>
                        </button>
                      </div>
                    </div>
                  );
                })
              }
            </div>

            {/* Desktop table */}
            <div className="desktop-table table-wrap">
              <table>
                <thead>
                  <tr>
                    <th>ID</th><th>Customer</th><th>Service</th><th>Branch</th>
                    <th>Pickup</th><th>Dropoff</th><th>Price</th><th>Status</th>
                    <th>Payment</th><th>Date</th><th></th>
                  </tr>
                </thead>
                <tbody>
                  {visible.length === 0
                    ? <tr><td colSpan={10}><div className="empty-state"><p>No orders found</p></div></td></tr>
                    : visible.map(o => {
                      const isForeign = isBranchMgr && !o.is_my_branch;
                      return (
                        <tr key={o.id} style={{background:isForeign?"rgba(90,90,114,0.04)":o.status==="needs_assignment"?"rgba(239,68,68,0.04)":undefined}}>
                          <td className="mono" style={{fontWeight:700,color:o.status==="needs_assignment"?"var(--red)":undefined}}>#{o.id}</td>
                          <td>
                            <div className="fw-bold" style={{fontSize:13}}>{o.customer_name||"—"}</div>
                            <div style={{fontSize:11,color:"var(--text3)"}}>{o.customer_phone}</div>
                          </td>
                          <td style={{textTransform:"capitalize",color:"var(--text2)"}}>{o.service_type}</td>
                          <td><BranchBadge branchName={o.branch_name||(o.branch_id?`Branch #${o.branch_id}`:null)} isMyBranch={!!o.is_my_branch} compact/></td>
                          <td style={{maxWidth:110,overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap",fontSize:12}}>{o.pickup_address}</td>
                          <td style={{maxWidth:110,overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap",fontSize:12}}>{o.dropoff_address}</td>
                          <td className="mono" style={{color:"var(--accent)",fontWeight:700}}>GHS {parseFloat(o.price||0).toFixed(2)}</td>
                          <td>{statusBadge(o.status)}</td>
                          <td>{paymentBadge(o.payment_status, o.payment_method)}</td>
                          <td className="mono" style={{fontSize:11,color:"var(--text3)"}}>{o.created_at?format(new Date(o.created_at),"MMM d, HH:mm"):"—"}</td>
                          <td>
                            <button className="btn btn-ghost btn-icon btn-sm" onClick={()=>{setSelected({...o,isForeign});setNewStatus(o.status);}}>
                              <Eye size={14}/>
                            </button>
                          </td>
                        </tr>
                      );
                    })
                  }
                </tbody>
              </table>
            </div>

            <div className="pagination">
              <span>Showing {visible.length} of {total}</span>
              <div className="pagination-btns">
                <button className="btn btn-ghost btn-sm" disabled={page<=1} onClick={()=>setPage(p=>p-1)}>Prev</button>
                <span style={{padding:"0 8px",color:"var(--text2)"}}>Page {page} of {pages||1}</span>
                <button className="btn btn-ghost btn-sm" disabled={page>=pages} onClick={()=>setPage(p=>p+1)}>Next</button>
              </div>
            </div>
          </>
        )}
      </div>

      {/* Order detail modal */}
      {selected && (
        <div className="modal-overlay" onClick={()=>setSelected(null)}>
          <div className="modal" onClick={e=>e.stopPropagation()}>
            <div className="modal-header">
              <div style={{display:"flex",alignItems:"center",gap:10,flexWrap:"wrap"}}>
                <div className="modal-title">Order #{selected.id}</div>
                <BranchBadge branchName={selected.branch_name||(selected.branch_id?`Branch #${selected.branch_id}`:null)} isMyBranch={!!selected.is_my_branch}/>
              </div>
              <button className="btn btn-ghost btn-icon btn-sm" onClick={()=>setSelected(null)}><X size={16}/></button>
            </div>
            {selected.isForeign && (
              <div style={{margin:"0 24px",background:"rgba(90,90,114,0.12)",border:"1px solid rgba(90,90,114,0.25)",borderRadius:8,padding:"10px 14px",fontSize:12,color:"var(--text2)",display:"flex",gap:8,alignItems:"center"}}>
                <span>🏢</span>
                <span>This order belongs to <strong style={{color:"var(--text)"}}>{selected.branch_name||"another branch"}</strong>. View only.</span>
              </div>
            )}
            <div className="modal-body">
              <div style={{display:"grid",gap:12}}>
                <Row label="Status"       value={statusBadge(selected.status)}/>
                <Row label="Service"      value={<span style={{textTransform:"capitalize"}}>{selected.service_type}</span>}/>
                <Row label="Branch"       value={<BranchBadge branchName={selected.branch_name} isMyBranch={!!selected.is_my_branch}/>}/>
                <Row label="Customer"     value={selected.customer_name||"—"}/>
                <Row label="Rider"        value={selected.rider_name||<span style={{color:"var(--red)"}}>Not assigned</span>}/>
                <Row label="Pickup"       value={selected.pickup_address}/>
                <Row label="Dropoff"      value={selected.dropoff_address}/>
                <Row label="Price"        value={<span style={{color:"var(--accent)",fontWeight:700}}>GHS {parseFloat(selected.price||0).toFixed(2)}</span>}/>
                <Row label="Payment"      value={paymentBadge(selected.payment_status, selected.payment_method)}/>
                {selected.paystack_reference && (
                  <Row label="Txn Ref" value={<span className="mono" style={{fontSize:11,color:"var(--text3)"}}>{selected.paystack_reference}</span>}/>
                )}
                <Row label="Distance"     value={`${selected.distance_km||0} km`}/>
                {selected.notes        && <Row label="Notes"   value={selected.notes}/>}
                {selected.b2b_reference && <Row label="B2B Ref" value={<span className="mono">{selected.b2b_reference}</span>}/>}
                <Row label="OTP Verified" value={selected.otp_verified?"✅ Yes":"❌ No"}/>
              </div>
              {!selected.isForeign && (
                <div style={{marginTop:20}}>
                  <label className="form-label">Update Status</label>
                  <div style={{display:"flex",gap:8,flexWrap:"wrap"}}>
                    <select className="select" style={{flex:1,minWidth:160}} value={newStatus} onChange={e=>setNewStatus(e.target.value)}>
                      {STATUS_OPTIONS.filter(Boolean).map(s=>(
                        <option key={s} value={s}>{s.replace(/_/g," ")}</option>
                      ))}
                    </select>
                    <button className="btn btn-primary" onClick={handleStatusUpdate} disabled={newStatus===selected.status}>Update</button>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

const Row = ({ label, value }) => (
  <div style={{display:"flex",justifyContent:"space-between",alignItems:"flex-start",gap:12,fontSize:13}}>
    <span style={{color:"var(--text3)",flexShrink:0}}>{label}</span>
    <span style={{textAlign:"right"}}>{value}</span>
  </div>
);